"use client";
import { Col, Container, Row } from "react-bootstrap";
import "../app/pricing/price.css";
import Image from "next/image";
import { Check } from "@/components/svg";
import CommonButton from "@/components/CommonButton";
import Testimonials from "@/components/Testimonials";
import customPricing from "../../public/assets/images/pricing/custom-price.png";
import price from "../../public/assets/images/pricing/price.png";
import PricingDetailsForm from "@/components/PricingDetailsForm";
import OtpModal from "@/components/OtpModal";
import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { getQuestionOption, verifyToken } from "@/services/pricing";
import AboutHeroSection from "@/components/AboutHeroSection";
import Link from "next/link";
import { event } from "@/lib/analytics";
const PricingCard = ({
  title,
  subtitle,
  price,
  unit,
  idealFor,
  benefits,
  idx,
  buttonText,
  handelButtonClick,
  featuredText = "",
}) => {
  return (
    <Col
      xl={3}
      lg={6}
      sm={6}
      key={idx}
      className={`p-xl-0 m-xl-0 d-flex pricing-card-main ${
        featuredText ? "best-plan" : ""
      }`}
    >
      <div
        className={`pricing-card ${featuredText ? "best-plan-responsive" : ""}`}
      >
        {featuredText && <div className="best-plan-inner">{featuredText}</div>}
        <div className="card-inner">
          <div>
            <h3 className="card-title">{title}</h3>
            <p className="card-subtitle">{subtitle}</p>
            {/* <Link href={`/packages`}> */}
            <CommonButton
              label={buttonText}
              onClick={handelButtonClick}
              className="w-100 mt-3"
            />
            {/* </Link> */}
          </div>
          <div>
            <p className="card-starting-at">Starting from just*</p>
            <div className="card-price">
              ${price}
              <sub className="card-unit">{unit}</sub>
            </div>
            <p className="card-ideal">
              <span>Ideal for </span>
              {idealFor}
            </p>
            <div className="card-benefits">
              {benefits?.map((item, idx) => (
                <p key={idx} className="benefit-item">
                  <Check className="check" />
                  {item}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Col>
  );
};
const cardData = [
  {
    title: "Hourly ⏲️",
    subtitle: "This model charges per hour worked, ideal for ongoing updates.",
    price: 10,
    unit: "/Hour",
    idealFor: "Existing Projects",
    benefits: [
      "Pay only for actual hours worked",
      "Ideal for flexible and evolving tasks",
      "Scalable and cost-effective",
      "Transparent tracking and reporting",
      "Best for ongoing updates and short-term tasks",
    ],
    buttonText: "Get Hourly Quote",
  },
  {
    title: "Fixed/Monthly Price 📆",
    subtitle:
      "It ensures clear costs and timelines based on the defined scope and design.",
    price: 1599,
    unit: "/Month",
    idealFor: "New Projects",
    benefits: [
      "Set cost for defined work scope",
      "Best for long-term projects",
      "Predictable budgeting, no hidden fees",
      "Dedicated resources for steady progress",
    ],
    featuredText: "Best plan for long term 🚀",
    buttonText: "Start Monthly Plan Discussion",
  },
  {
    title: "Custom Pricing 🔥",
    subtitle:
      "Get an instant, tailored project quote based on your exact needs — team, tech, and timeline.",
    price: 999,
    unit: "/Project",
    idealFor: "New Projects",
    benefits: [
      "Projects with Specific Team or Tech Stack Requirements",
      "Budget-Driven Planning With Cost Breakdown",
      "Flexible Scope and Timeline Adjustments",
      "Great for MVPs, SaaS, Fintech, and AI Projects",
    ],
    buttonText: "✨ Calculate My Project",
  },
  {
    title: "Maintenance ⚙️",
    subtitle: "Ideal for seamless and worry-free website maintenance.",
    price: 599,
    unit: "/Month",
    idealFor: "Website Maintenance",
    benefits: [
      "Regular updates and security patches",
      "Performance monitoring and optimization",
      "Backup and recovery support",
      "Ensures smooth, hassle-free operation",
    ],
    buttonText: "Talk Website Maintenance",
  },
];
const pricingData = [
  {
    title: "Frontend Development",
    platforms: ["Cross-platform", "Web", "Desktop", "Game"],
    price: "$10-20",
  },
  {
    title: "Backend Development",
    platforms: ["Web"],
    price: "$10-20",
  },
  {
    title: "Full Stack Development",
    platforms: ["Cross-platform", "Web", "Desktop"],
    price: "$17-25",
  },
  {
    title: "UI/UX Design",
    platforms: ["Mobile", "Web", "Desktop App"],
    price: "$10-25",
  },
  {
    title: "Mobile Development",
    platforms: ["Mobile App", "Cross-platform"],
    price: "$10-25",
  },
];
const tableHeaders = ["Simple Page", "Regular Page", "Complex Page"];

const pageData = [
  {
    label: "Price starts from",
    className: "price",
    values: ["$180(USD)", "$380(USD)", "$580(USD)"],
  },
  {
    label: "Layout type",
    className: "value",
    values: ["Basic Layout", "Standard Layout", "Detailed Layout"],
  },
  {
    label: "Number of sections",
    className: "alt-value",
    values: ["1-3 Sections", "3-6 Sections", "6-9 Sections"],
  },
  {
    label: "Functionality type",
    className: "value",
    values: ["Standard", "Custom", "Advanced"],
  },
  {
    label: "Page examples",
    className: "alt-value",
    values: ["About, Contact", "Services, Products", "Home, Membership"],
  },
];

const transposedCards = tableHeaders.map((title, colIndex) => ({
  title,
  price: pageData[0].values[colIndex],
  details: pageData.slice(1).map((row) => ({
    label: row.label,
    value: row.values[colIndex],
  })),
}));
export default function PricePage() {
  const [modalShow, setModalShow] = useState(false);
  const router = useRouter();
  const customPriceRef = useRef(null);
  const pathname = usePathname();
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const scrollToSection = () => {
      if (hash === "custom-pricing" && customPriceRef.current) {
        customPriceRef.current.scrollIntoView({ behavior: "smooth" });
      }
    };
    const timeout = setTimeout(scrollToSection, 300);
    return () => clearTimeout(timeout);
  }, [pathname]);
  const [packages, setPackages] = useState([]);
  const renderImageColumn = (imgList) => (
    <div className="d-flex flex-column gap-3">
      {imgList?.map((img, index) => (
        <div className="img-container" key={index}>
          <Image src={img} alt={`tech-logo-${index}`} className="img-fluid" />
        </div>
      ))}
    </div>
  );

  // const fetchApi = async () => {
  //   try {
  //     const [packageRes] = await Promise.all(
  //       ["package"]?.map((item) => getQuestionOption(item))
  //     );
  //     setPackages(packageRes);
  //   } catch (error) {
  //   }
  // };
  // useEffect(() => {
  //   fetchApi();
  // }, []);

  return (
    <>
      <AboutHeroSection
        pillText=" ~ Pricing ~"
        title="Quality Services at"
        highlight="Reasonable Pricing"
        description=" We offer high-quality services at competitive rates, ensuring
                the perfect balance between cost and excellence. Our transparent
                pricing structure eliminates hidden charges, giving you clarity
                and confidence. With a focus on delivering value-driven
                solutions, we help you achieve your goals within budget."
        image={price}
        imageAlt="Quality Services at Reasonable Pricing"
      />
      <section className="py-5">
        <Container>
          <Row className="justify-content-center pb-5">
            <Col xl={8} md={12} className="text-center">
              <div className="com-pill mx-auto mb-4 ">
                Affordable Pricing Plans
              </div>
              <p>
                We offer transparent and budget-friendly pricing plans tailored
                to your needs. Choose a plan that fits your project requirements
                without any hidden costs.
              </p>
            </Col>
          </Row>
          <Row className="mt-5 gy-4">
            {cardData?.length > 0 &&
              cardData?.map((card, idx) => (
                <PricingCard
                  {...card}
                  key={idx}
                  handelButtonClick={async () => {
                    if (card?.buttonText === "✨ Calculate My Project") {
                      try {
                        const isAuthDetails =
                          !!localStorage.getItem("authDetails");
                        if (!isAuthDetails) {
                          setModalShow(true);
                          return;
                        }
                        const isAuthenticated = await verifyToken();
                        if (isAuthenticated) {
                          router.replace("/custom-pricing");
                        } else {
                          setModalShow(true);
                        }
                      } catch (error) {
                        setModalShow(true);
                      }
                      event({
                        action: "custom pricing",
                        params: {
                          page: "pricing_page",
                          plans_name: card?.title,
                        },
                      });
                    } else {
                      router.replace("/contact#inquiry");
                      event({
                        action: "pricing",
                        params: {
                          page: "pricing_page",
                          plans_name: card?.title,
                        },
                      });
                    }
                  }}
                />
              ))}
          </Row>
        </Container>
      </section>
      <section className="hourly-pricing mt-5">
        <Container>
          <Row className="justify-content-center">
            <Col xl={9} md={12} className="text-center">
              <h2 className="com-title">
                <span>Hourly Pricing </span> Structure for Technology Stack
              </h2>
              <p>
                Our hourly pricing model ensures transparency and flexibility,
                allowing you to pay based on actual development hours. Rates
                vary depending on the technology stack, project complexity, and
                expertise required. This approach is ideal for dynamic projects
                that need scalability and ongoing enhancements.
              </p>
            </Col>
            <Col lg={12} className="mt-5">
              <div className="table-wrapper">
                <table className="tech-table table-striped">
                  <thead>
                    <tr className="odd">
                      <th>Technology Stack</th>
                      <th>Platform/Product</th>
                      <th>Average Hourly Cost (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingData.map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? "even" : "odd"}>
                        <td>{item.title}</td>
                        <td>
                          <ul>
                            {item.platforms.map((p, i) => (
                              <li key={i}>{p}</li>
                            ))}
                          </ul>
                        </td>
                        <td>{item.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="card-grid">
                  {pricingData.map((item, idx) => (
                    <div key={idx} className="pricing-hr-card">
                      <div className="pricing-hr-card-header">
                        <h3 className="p-0 m-0">{item.title}</h3>
                        <p className="p-0 m-0">
                          <span className="primary-color">{item.price}</span>
                          <sub>/Hour</sub>
                        </p>
                      </div>
                      <div className="pricing-hr-card-body">
                        <h4>Platform/Product:</h4>
                        <ul>
                          {item.platforms.map((p, i) => (
                            <li key={i}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  href="/contact#inquiry"
                  aria-label={`Contact Us for more Details`}
                >
                  <CommonButton
                    label="Select & Contact Us"
                    className="d-block mx-auto mt-5"
                    onClick={() =>
                      event({
                        action: "pricing",
                        params: {
                          page: "pricing_page",
                        },
                      })
                    }
                  />
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      {/* <section className="fixed-cost">
        <Container>
          <Row className="justify-content-center">
            <Col xl={9} md={12} className="text-center">
              <h2 className="com-title">
                Fixed-Cost Solutions for{" "}
                <span>Development & Design + Development </span>
              </h2>
              <p>
                Our fixed-cost model ensures transparency and predictability,
                allowing you to plan your budget with confidence. Whether you
                need design, development, or both, we deliver high-quality
                solutions without hidden costs.
              </p>
            </Col>
            <Col xl={12} className="mt-5">
              <div className="wrapper">
                <table className="desktop-table">
                  <thead>
                    <tr>
                      <th className="table-header"></th>
                      {tableHeaders.map((header, index) => (
                        <th key={index} className="table-header">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        <td className="label">{row.label}</td>
                        {row.values.map((value, colIndex) => (
                          <td key={colIndex} className={row.className}>
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mobile-view">
                  <div className="cards-wrapper">
                    {transposedCards.map((card, idx) => (
                      <div className="fixed-cost-card" key={idx}>
                        <div className="fixed-cost-card-header">
                          <h3 className="p-0 m-0">{card.title}</h3>
                          <p className="p-0 m-0">{card.price}</p>
                        </div>
                        <div className="fixed-cost-card-body">
                          {card.details.map((d, i) => (
                            <div className="fixed-cost-card-detail" key={i}>
                              <h4 className="p-0 m-0">{d.label}:</h4>
                              <p className="p-0 m-0">{d.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section> */}
      <Testimonials />
      <section
        className="custom-price"
        id="custom-pricing"
        ref={customPriceRef}
      >
        <Container>
          <Row className="gy-4">
            <Col xl={6} md={6} sm={12} className="align-self-center">
              <Image
                src={customPricing}
                alt="customPricing"
                className="img-fluid"
              />
            </Col>
            <Col xl={6} md={6} sm={12} className="align-self-center">
              <div className="com-pill mb-4">Custom Pricing ~</div>
              <h3 className="com-title mb-4">
                See how much your project would cost,{" "}
                <span>Try our 1-min calculator</span>
              </h3>
              <p className="">
                Every business is different — and so is every project. Use our
                interactive estimator to define your industry, timeline,
                complexity, and technical needs. Get matched with tailored
                solutions that fit your vision and goals.
              </p>
              <p>
                🔍 Start with a few simple questions to help us understand your
                project requirements.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <CommonButton
                  onClick={async () => {
                    try {
                      const isAuthDetails =
                        !!localStorage.getItem("authDetails");
                      if (!isAuthDetails) {
                        setModalShow(true);
                        return;
                      }
                      const isAuthenticated = await verifyToken();
                      if (isAuthenticated) {
                        router.replace("/custom-pricing");
                      } else {
                        setModalShow(true);
                      }
                    } catch (error) {
                      setModalShow(true);
                    }
                  }}
                  label="Get Started"
                  className="btn-primary free-consultation"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                      style={{ height: "20px", fill: "#fff" }}
                    >
                      <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
                    </svg>
                  }
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section className="py-5">
        <Container>
          <Row className="justify-content-center pb-5">
            <Col xl={12} md={12} className="text-center">
              <div className="com-pill mx-auto mb-4 ">Find My Quote</div>
              <h3 className="com-title mb-4">
                Already received <span>Quote?</span>
              </h3>
              <p>
                If you’ve previously requested a quote from us, you don’t need
                to go through the entire process again. Just enter the email
                address you used at the time of the request, along with the
                unique quote ID we provided you via email. This will instantly
                pull up your personalized quote, allowing you to review pricing,
                project scope, or next steps—exactly as it was shared with you.
                It’s a fast, secure, and convenient way to access your quote
                whenever you need it.
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <CommonButton
                  label="Fetch Your Quote"
                  onClick={() => router.push("/find-quote")}
                  className="btn-primary free-consultation"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                      style={{ height: "20px", fill: "#fff" }}
                    >
                      <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
                    </svg>
                  }
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <PricingDetailsForm modalShow={modalShow} setModalShow={setModalShow} />
    </>
  );
}
